import { useCallback, useMemo } from 'react'

import {
  useChatOrgId,
  useChatSessionId,
} from '@/components/chat-v2/hooks/useChatEnvironment'
import {
  useOpportunityChatSession,
  useOrganizationChatSession,
  usePursuitChatSession,
  usePursuitDocumentChatSession,
} from '@/components/chat-v2/hooks/useChatSession'

import type {
  OpportunityChatSession,
  OrganizationChatSession,
  PursuitChatSession,
  PursuitDocumentChatSession,
  Source,
} from '@/types/chat-v2'

const normalizeSourceId = (sourceId: number | string | null) => {
  if (sourceId == null) return null
  if (typeof sourceId === 'number') return sourceId
  const parsed = Number(sourceId)
  return Number.isNaN(parsed) ? null : parsed
}

// region: Single Source Hooks

interface UseSessionSourceProps {
  sourceId: number | string | null
  enabled?: boolean
}

export const usePursuitSessionSource = ({
  sourceId,
  enabled = true,
}: UseSessionSourceProps) => {
  const sessionId = useChatSessionId()
  const orgId = useChatOrgId()
  const normalizedSourceId = normalizeSourceId(sourceId)

  const selectSource = useCallback(
    (session: PursuitChatSession) => {
      if (normalizedSourceId == null) return undefined
      return session.sources.find((source) => source.id === normalizedSourceId)
    },
    [normalizedSourceId],
  )

  return usePursuitChatSession({
    orgId,
    sessionId: sessionId ?? '',
    select: selectSource,
    enabled: enabled && normalizedSourceId != null,
  })
}

export const usePursuitDocumentSessionSource = ({
  sourceId,
  enabled = true,
}: UseSessionSourceProps) => {
  const sessionId = useChatSessionId()
  const orgId = useChatOrgId()
  const normalizedSourceId = normalizeSourceId(sourceId)

  const selectSource = useCallback(
    (session: PursuitDocumentChatSession) => {
      if (normalizedSourceId == null) return undefined
      return session.sources.find((source) => source.id === normalizedSourceId)
    },
    [normalizedSourceId],
  )

  return usePursuitDocumentChatSession({
    orgId,
    sessionId: sessionId ?? '',
    select: selectSource,
    enabled: enabled && normalizedSourceId != null,
  })
}

export const useOpportunitySessionSource = ({
  sourceId,
  enabled = true,
}: UseSessionSourceProps) => {
  const sessionId = useChatSessionId()
  const orgId = useChatOrgId()
  const normalizedSourceId = normalizeSourceId(sourceId)

  const selectSource = useCallback(
    (session: OpportunityChatSession) => {
      if (normalizedSourceId == null) return undefined
      return session.sources.find((source) => source.id === normalizedSourceId)
    },
    [normalizedSourceId],
  )

  return useOpportunityChatSession({
    orgId,
    sessionId: sessionId ?? '',
    select: selectSource,
    enabled: enabled && normalizedSourceId != null,
  })
}

export const useOrganizationSessionSource = ({
  sourceId,
  enabled = true,
}: UseSessionSourceProps) => {
  const sessionId = useChatSessionId()
  const orgId = useChatOrgId()
  const normalizedSourceId = normalizeSourceId(sourceId)

  const selectSource = useCallback(
    (session: OrganizationChatSession) => {
      if (normalizedSourceId == null) return undefined
      return session.sources.find((source) => source.id === normalizedSourceId)
    },
    [normalizedSourceId],
  )

  return useOrganizationChatSession({
    orgId,
    sessionId: sessionId ?? '',
    select: selectSource,
    enabled: enabled && normalizedSourceId != null,
  })
}

// endregion: Single Source Hooks

// region: Sources Map Hooks (per session type)

export type SourcesMap = Map<number, Source>

interface UseSessionSourcesMapProps {
  sourceIds: number[] | null | undefined
}

/** Build a Map of sourceId -> Source from a session's sources array */
const buildSourcesMap = <T extends { sources: Source[] }>(
  session: T,
  normalizedIds: number[],
): SourcesMap => {
  if (normalizedIds.length === 0) return new Map()
  const idsSet = new Set(normalizedIds)
  const map = new Map<number, Source>()
  for (const source of session.sources) {
    if (idsSet.has(source.id)) {
      map.set(source.id, source)
      if (map.size === idsSet.size) break
    }
  }
  return map
}

/** Normalize source IDs from number[] to filtered number[] */
const useNormalizedSourceIds = (
  sourceIds: number[] | null | undefined,
): number[] =>
  useMemo(
    () =>
      sourceIds
        ?.map((id) => normalizeSourceId(id))
        .filter((id): id is number => id !== null) ?? [],
    [sourceIds],
  )

export const useOrganizationChatSessionSourcesMap = ({
  sourceIds,
}: UseSessionSourcesMapProps) => {
  const sessionId = useChatSessionId()
  const orgId = useChatOrgId()
  const normalizedIds = useNormalizedSourceIds(sourceIds)

  const selectSourcesMap = useCallback(
    (session: OrganizationChatSession) =>
      buildSourcesMap(session, normalizedIds),
    [normalizedIds],
  )

  return useOrganizationChatSession({
    orgId,
    sessionId: sessionId ?? '',
    select: selectSourcesMap,
  })
}

export const usePursuitChatSessionSourcesMap = ({
  sourceIds,
}: UseSessionSourcesMapProps) => {
  const sessionId = useChatSessionId()
  const orgId = useChatOrgId()
  const normalizedIds = useNormalizedSourceIds(sourceIds)

  const selectSourcesMap = useCallback(
    (session: PursuitChatSession) => buildSourcesMap(session, normalizedIds),
    [normalizedIds],
  )

  return usePursuitChatSession({
    orgId,
    sessionId: sessionId ?? '',
    select: selectSourcesMap,
  })
}

export const usePursuitDocumentChatSessionSourcesMap = ({
  sourceIds,
}: UseSessionSourcesMapProps) => {
  const sessionId = useChatSessionId()
  const orgId = useChatOrgId()
  const normalizedIds = useNormalizedSourceIds(sourceIds)

  const selectSourcesMap = useCallback(
    (session: PursuitDocumentChatSession) =>
      buildSourcesMap(session, normalizedIds),
    [normalizedIds],
  )

  return usePursuitDocumentChatSession({
    orgId,
    sessionId: sessionId ?? '',
    select: selectSourcesMap,
  })
}

export const useOpportunityChatSessionSourcesMap = ({
  sourceIds,
}: UseSessionSourcesMapProps) => {
  const sessionId = useChatSessionId()
  const orgId = useChatOrgId()
  const normalizedIds = useNormalizedSourceIds(sourceIds)

  const selectSourcesMap = useCallback(
    (session: OpportunityChatSession) =>
      buildSourcesMap(session, normalizedIds),
    [normalizedIds],
  )

  return useOpportunityChatSession({
    orgId,
    sessionId: sessionId ?? '',
    select: selectSourcesMap,
  })
}

// endregion: Sources Map Hooks
